import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Building2, Users, LinkIcon, Copy, Plus, Loader2, Trash2, CreditCard, Workflow, Bell } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const navigate = useNavigate();
  const { user, business, refresh } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const [biz, setBiz] = useState(null);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const [memberDialog, setMemberDialog] = useState(false);
  const [member, setMember] = useState({ name: "", email: "", password: "", role: "staff" });

  const loadTeam = useCallback(() => { api.get("/business/team").then(({ data }) => setTeam(data)); }, []);
  useEffect(() => { api.get("/business").then(({ data }) => setBiz(data)); loadTeam(); }, [loadTeam]);

  const setField = (k) => (e) => setBiz({ ...biz, [k]: e.target.value });

  const saveBiz = async () => {
    setSaving(true);
    try {
      await api.put("/business", { name: biz.name, owner_name: biz.owner_name, email: biz.email, phone: biz.phone, website: biz.website, address: biz.address, zip_code: biz.zip_code });
      await refresh();
      toast.success("Business info saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addMember = async () => {
    if (!member.name || !member.email || !member.password) { toast.error("All fields required"); return; }
    try {
      await api.post("/business/team", member);
      toast.success("Team member added");
      setMemberDialog(false);
      setMember({ name: "", email: "", password: "", role: "staff" });
      loadTeam();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changeRole = async (id, role) => { await api.put(`/business/team/${id}/role`, { role }); loadTeam(); };
  const removeMember = async (id) => { await api.delete(`/business/team/${id}`); toast.success("Removed"); loadTeam(); };

  const formUrl = business ? `${window.location.origin}/book/${business.slug}` : "";
  const copyUrl = () => { navigator.clipboard.writeText(formUrl); toast.success("Link copied"); };

  if (!biz) return <div className="space-y-4"><h1 className="text-2xl font-bold font-heading">Settings</h1></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your workspace.</p>
      </div>

      <Tabs defaultValue="business">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="business" data-testid="tab-business"><Building2 className="h-4 w-4 mr-1" /> Business</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team"><Users className="h-4 w-4 mr-1" /> Team & Roles</TabsTrigger>
          <TabsTrigger value="form" data-testid="tab-form"><LinkIcon className="h-4 w-4 mr-1" /> Lead Form</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="mt-4">
          <Card className="p-6 border-slate-200 max-w-2xl">
            <h3 className="font-heading font-semibold mb-4">Business information</h3>
            <div className="space-y-4">
              <div><Label>Business name</Label><Input value={biz.name || ""} onChange={setField("name")} data-testid="settings-biz-name" className="mt-1" disabled={!canManage} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Owner name</Label><Input value={biz.owner_name || ""} onChange={setField("owner_name")} data-testid="settings-owner" className="mt-1" disabled={!canManage} /></div>
                <div><Label>Business email</Label><Input value={biz.email || ""} onChange={setField("email")} data-testid="settings-email" className="mt-1" disabled={!canManage} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={biz.phone || ""} onChange={setField("phone")} data-testid="settings-phone" className="mt-1" disabled={!canManage} /></div>
                <div><Label>Website</Label><Input value={biz.website || ""} onChange={setField("website")} data-testid="settings-website" className="mt-1" disabled={!canManage} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Address</Label><Input value={biz.address || ""} onChange={setField("address")} data-testid="settings-address" className="mt-1" disabled={!canManage} /></div>
                <div><Label>ZIP</Label><Input value={biz.zip_code || ""} onChange={setField("zip_code")} data-testid="settings-zip" className="mt-1" disabled={!canManage} /></div>
              </div>
              {canManage && <Button onClick={saveBiz} disabled={saving} data-testid="save-biz-btn" className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Team members</h3>
              {canManage && <Button onClick={() => setMemberDialog(true)} data-testid="add-member-btn" className="bg-blue-600 hover:bg-blue-700" size="sm"><Plus className="h-4 w-4 mr-1" /> Add member</Button>}
            </div>
            <div className="space-y-2">
              {team.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100" data-testid={`member-${m.id}`}>
                  <div>
                    <div className="font-medium">{m.name} {m.id === user.id && <span className="text-xs text-slate-400">(you)</span>}</div>
                    <div className="text-sm text-slate-500">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.role === "owner" || !canManage || m.id === user.id ? (
                      <Badge variant="secondary" className="capitalize">{m.role}</Badge>
                    ) : (
                      <>
                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                          <SelectTrigger className="h-8 w-28" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent>
                        </Select>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-red-500" data-testid={`remove-member-${m.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Remove {m.name}?</AlertDialogTitle><AlertDialogDescription>They will lose access to this workspace.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => removeMember(m.id)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-slate-400 space-y-1">
              <p><strong>Owner</strong> — full access incl. billing. <strong>Admin</strong> — manage leads, team, automations. <strong>Staff</strong> — manage leads, customers, appointments.</p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="form" className="mt-4">
          <Card className="p-6 border-slate-200 max-w-2xl">
            <h3 className="font-heading font-semibold mb-2">Public lead capture form</h3>
            <p className="text-sm text-slate-500 mb-4">Share this link on your website, Google profile or ads. Submissions land directly in your pipeline.</p>
            <div className="flex gap-2">
              <Input value={formUrl} readOnly data-testid="form-url" className="font-mono text-sm" />
              <Button onClick={copyUrl} variant="outline" data-testid="copy-form-url"><Copy className="h-4 w-4" /></Button>
            </div>
            <a href={formUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="mt-3" data-testid="preview-form"><LinkIcon className="h-4 w-4 mr-1" /> Preview form</Button></a>
          </Card>
        </TabsContent>

        <TabsContent value="account" className="mt-4">
          <Card className="p-6 border-slate-200 max-w-2xl space-y-4">
            <h3 className="font-heading font-semibold">Account</h3>
            <div><Label>Name</Label><Input value={user.name} readOnly className="mt-1" /></div>
            <div><Label>Email</Label><Input value={user.email} readOnly className="mt-1" /></div>
            <div><Label>Role</Label><div className="mt-1"><Badge variant="secondary" className="capitalize">{user.role}</Badge></div></div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button variant="outline" onClick={() => navigate("/app/billing")} data-testid="account-billing"><CreditCard className="h-4 w-4 mr-1" /> Billing</Button>
              <Button variant="outline" onClick={() => navigate("/app/automations")} data-testid="account-automations"><Workflow className="h-4 w-4 mr-1" /> Automations</Button>
              <Button variant="outline" onClick={() => navigate("/app/notifications")} data-testid="account-notifications"><Bell className="h-4 w-4 mr-1" /> Notifications</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={memberDialog} onOpenChange={setMemberDialog}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} data-testid="member-name" className="mt-1" /></div>
            <div><Label>Email</Label><Input type="email" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} data-testid="member-email" className="mt-1" /></div>
            <div><Label>Temporary password</Label><Input value={member.password} onChange={(e) => setMember({ ...member, password: e.target.value })} data-testid="member-password" className="mt-1" placeholder="At least 8 characters" /></div>
            <div>
              <Label>Role</Label>
              <Select value={member.role} onValueChange={(v) => setMember({ ...member, role: v })}>
                <SelectTrigger className="mt-1" data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberDialog(false)}>Cancel</Button>
            <Button onClick={addMember} data-testid="save-member-btn" className="bg-blue-600 hover:bg-blue-700">Add member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
